<template>
  <div class="py-4 container-fluid">
    <div class="row">
      <div class="mx-auto col-lg-12 col-12">
        <div class="mt-4 card card-body">
          <div id="invoice" class="invoice">
            <div class="row">
              <div class="col-6">
                <div class="company-details">
                  <div class="logo-container">
                    <img src="@/assets/img/smartintrade-logo-new.png" alt="Logo" class="company-logo">
                    <h3 class="company-name">PT. Smartin Advisor Sistem</h3>
                  </div>
                  <p class="contact-info">+62 821 1830 6880 | admin@smartintrade.co.id</p>
                </div>
              </div>
              <div class="col-6 text-right">
                <h2 class="invoice-title">INVOICE</h2>
                <p class="invoice-date"><strong>Date:</strong> {{ data.date }}</p>
                <p class="invoice-no"><strong>Invoice No.:</strong> {{ data.invoiceCustomer }}</p>
              </div>
            </div>
            <div class="row">
              <div class="col-6">
                <h4 class="section-title">Bill To</h4>
                <p>{{ data.billTo.contactName }}</p>
                <p>{{ data.billTo.phone }}</p>
                <p>{{ data.billTo.email }}</p>
              </div>
            </div>
            <div class="row">
              <div class="table-wrapper">
                <table class="table table-bordered">
                  <thead>
                    <tr>
                      <th class="text-left text-dark p-3">Description</th>
                      <th class="text-left text-dark p-3">Subscription</th>
                      <th class="text-left text-dark p-3">Quantity</th>
                      <th class="text-left text-dark p-3">Unit Price</th>
                      <th class="text-left text-dark p-3">Total</th>
                    </tr>
                  </thead>
                  <tbody v-if="data.items.length > 0">
                    <tr v-for="(item, index) in data.items" :key="index">
                      <td class="text-muted text-subtitle-1 description-column">
                        <p class="m-0">{{ item.description ?? "-" }}</p>
                        <p class="m-0 text-xs text-gray-500">{{ data.paidVia }}</p>
                      </td>
                      <td class="text-muted text-subtitle-1 p-3">{{ data.timeframe }}</td>
                      <td class="text-muted text-subtitle-1 p-3">{{ item.qty ?? "-" }}</td>
                      <td class="text-muted text-subtitle-1 p-3">Rp. {{ formatPrice(item.unitPrice) ?? "-" }}</td>
                      <td class="text-muted text-subtitle-1 p-3">Rp. {{ formatPrice(item.total) ?? "-" }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <div class="row text-right summary">
              <p><strong>Subtotal: </strong> Rp. {{ formatPrice(data.subtotal) }}</p>
              <p><strong>Discount: </strong> Rp. {{ formatPrice(data.discount) }}</p>
              <p><strong>Tax 11%: </strong> Rp. {{ formatPrice(data.totalTax) }}</p>
              <!-- <p><strong>Sas Fee: </strong> {{data.sas_fee_type === "percentage" ? `${data.sas_fee}%` : `Rp. ${formatPrice(data.sas_fee || 0)}` }}</p> -->
              <p v-if="data.outstanding_in"><strong>Outstanding In %: </strong> Rp. {{ data.outstanding_in }}</p>
              <p v-if="data.base_price"><strong>Base Price: </strong> Rp. {{ formatPrice(data.base_price) }}</p>

              <h3 class="balance-due"><strong>Total Payment: </strong> Rp. {{ formatPrice(data.balanceDue) }}</h3>
              <!-- {{ data.sas_fee_type }} -->
            </div>
            <div class="row">
              <p>Remarks / Payment Instructions:</p>
            </div>
          </div>
          <button class="btn btn-primary mt-5 w-40 mx-auto" @click="generatePDF">Download PDF</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from "vue";
import { useRoute } from "vue-router";
import { toast } from "vue3-toastify";
import InvoiceService from "@/services/invoices.service.js";

const data = ref({
  date: new Date().toLocaleDateString("en-GB"),
  invoiceNo: "-",
  invoiceCustomer: "-",
  billTo: {
    contactName: "-",
    companyName: "-",
    address: "-",
    phone: "-",
    email: "-"
  },
  items: [],
  subtotal: 0,
  discount: 0,
  totalTax: 0,
  balanceDue: 0,
  paidVia: "",
  timeframe: "-",
  sas_fee: 0,
  sas_fee_type: "",
  base_price: null,
  outstanding_in: null,
});

const toastId = ref("");

export default {
  name: "TransactionInvoiceFile",
  setup() {
    const route = useRoute();

    const showLoading = () => {
      toastId.value = toast.loading("Please wait...");
    };

    const hideLoading = () => {
      toast.remove(toastId.value);
    };

    const showError = (message) => {
      toast.update(toastId.value, {
        render: message,
        type: "error",
        isLoading: false,
        autoClose: 3000
      });
    };

    const formatPrice = (price) => {
      return new Intl.NumberFormat("id-ID").format(price);
    };

    const getInvoiceData = async () => {
      showLoading();
      try {
        const response = await InvoiceService.get(route.params.id);
        const { invoice_data: invoiceData, subscription_data: subscriptionData } = response.data;

        data.value.date = invoiceData.created_at ? new Date(invoiceData.created_at).toLocaleDateString("en-GB") : "-";
        data.value.invoiceNo = invoiceData.id ?? "-";
        data.value.invoiceCustomer = invoiceData.display_invoice_id|| invoiceData.id  || '-';
        data.value.billTo.contactName = invoiceData.customer_name ?? "-";
        data.value.billTo.phone = invoiceData.phone_number ?? "-";
        data.value.billTo.email = invoiceData.email ?? "-";
        data.value.items = invoiceData.item_details ? invoiceData.item_details?.map(item => ({
          description: item.name,
          qty: item.quantity,
          unitPrice: item.price,
          total: item.price * item.quantity
        })) : [];
        data.value.subtotal = invoiceData.payment_amount ?? 0;
        data.value.balanceDue = invoiceData.payment_amount ?? 0;

        if (subscriptionData) {
          data.value.paidVia = subscriptionData.paid_via ? `Pembayaran via ${subscriptionData.paid_via}` : "";
          data.value.timeframe = subscriptionTimeframe(subscriptionData.active_from, subscriptionData.active_until);
        }

        data.value.sas_fee = invoiceData?.margin?.sas_fee
        data.value.sas_fee_type = invoiceData?.margin?.sas_fee_type

        const additionalData = JSON.parse(invoiceData.additional_param)
        // console.log(additionalData)
        data.value.outstanding_in = additionalData['outstanding_in']
        data.value.base_price = additionalData['base_price']

      } catch (error) {
        showError(error.response?.data?.code_message ?? "Error fetching invoice data");
      } finally {
        hideLoading();
      }
    };

    const subscriptionTimeframe = (start, end) => {
      if (start && end) {
        const startFormat = new Date(start).toLocaleDateString("en-GB");
        const endFormat = new Date(end).toLocaleDateString("en-GB");
        return `${startFormat} - ${endFormat}`;
      }
      return "-";
    };

    const generatePDF = async () => {
      const html2pdf = await import("html2pdf.js");
      const element = document.getElementById("invoice");

      html2pdf.default()
        .from(element)
        .set({
          margin: 0.25,
          filename: `${data.value.invoiceNo || "invoice"}.pdf`,
          image: { type: "jpeg", quality: 1 },
          html2canvas: {
            scale: 2,
            scrollX: 0,
            scrollY: 0,
            windowWidth: element.scrollWidth,
            windowHeight: element.scrollHeight
          },
          jsPDF: { unit: 'in', format: 'a4', orientation: 'portrait' },
        })
        .save();
    };

    onMounted(getInvoiceData);

    return {
      data,
      formatPrice,
      generatePDF
    };
  }
};
</script>

<style scoped>
.invoice {
  border: 1px solid #ccc;
  padding: 20px;
  border-radius: 8px;
  /* font-family:Rockwell,serif; */
}

.company-details {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.logo-container {
  display: flex;
  align-items: center;
  margin-bottom: 5px;
}

.company-logo {
  max-width: 50px;
  height: auto;
  margin-right: 10px;
}

.company-name {
  font-size: 16px;
  font-weight: bold;
  margin: 0;
}

.contact-info {
  font-size: 12px;
  color: #555;
  margin-top: 0;
}

.table-wrapper {
  width: 100%;
  overflow-x: auto;
}

table {
  width: 100%;
  border-collapse: collapse;
  page-break-inside: avoid;
}

table thead th {
  background-color: #abbb30;
  color: white;
  page-break-inside: avoid;
}

table tbody tr {
  border-bottom: 1px solid #ccc;
  page-break-inside: avoid;
}

table tbody td {
  padding: 8px;
  word-break: break-word;
  overflow-wrap: break-word;
  page-break-inside: avoid;
}

.text-right {
  text-align: right;
}

.invoice-title {
  font-size: 18px;
  font-weight: bold;
}

.invoice-date, .invoice-no {
  font-size: 9px;
}

.section-title {
  font-size: 18px;
  font-weight: bold;
  margin-bottom: 5px;
}

.summary p {
  margin: 5px 0;
}

.balance-due {
  font-size: 20px;
  font-weight: bold;
  margin-top: 20px;
}

.description-column {
    max-width: 280px;
    word-wrap: break-word;
    word-break: break-word;
    white-space: normal;
    line-height: 1.4;
}

@media print {
  body {
    margin: 0; /* Hapus margin di body */
    padding: 0; /* Hapus padding di body */
  }

  .invoice {
    margin: 0; /* Menghapus margin */
    padding: 0; /* Menghapus padding */
    transform: scale(0.9); /* Skala tabel ke 90% */
    transform-origin: top left; /* Tetapkan titik asal transformasi */
  }

  .table-wrapper {
    overflow: visible; /* Pastikan tabel terlihat */
    width: auto; /* Atur lebar otomatis */
  }

  table {
    width: 100vw; /* Gunakan lebar viewport untuk tabel */
    max-width: 100%; /* Pastikan tidak melebihi lebar maksimum */
  }

  th, td {
    min-width: 100px; /* Atur lebar minimum untuk kolom */
    page-break-inside: avoid; /* Hindari pemotongan di dalam sel */
    white-space: nowrap; /* Mencegah teks membungkus */
  }

  .summary, .table-wrapper {
    page-break-inside: avoid; /* Hindari pemotongan di dalam summary dan wrapper */
  }

  .no-print {
    display: none; /* Contoh: sembunyikan elemen dengan kelas no-print */
  }
}
</style>